import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, MapPin, User, Activity, TrendingUp, Bell, Sparkles, Mic } from 'lucide-react';
import Navbar from '@/components/Navbar';
import ExplainableAI from '@/components/ExplainableAI';
import ProgressChart from '@/components/ProgressChart';
import DynamicRecommendations from '@/components/DynamicRecommendations';
import { api } from '@/utils/api';
import { useLanguage } from '@/LanguageContext';

const ChildProfile = ({ user, onLogout }) => {
  const { childId } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [child, setChild] = useState(null);
  const [screenings, setScreenings] = useState([]);
  const [interventions, setInterventions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('screenings'); // 'screenings' | 'progress' | 'recommendations'
  const [sendingReminder, setSendingReminder] = useState(false);

  const handleSendReminder = async () => {
    if (!window.confirm(t('notifications.confirmSendReminder') || 'Send follow-up reminder to caregiver and frontline workers?')) return;
    
    setSendingReminder(true);
    try {
      await api.sendFollowUpReminder(childId);
      alert(t('notifications.reminderSent'));
    } catch (error) {
      console.error('Error sending reminder:', error);
      alert('Failed to send reminder');
    } finally {
      setSendingReminder(false);
    }
  };

  useEffect(() => {
    fetchChildData();
  }, [childId]);

  const fetchChildData = async () => {
    try {
      const [childRes, screeningsRes, interventionsRes] = await Promise.all([
        api.getChild(childId),
        api.getChildScreenings(childId),
        api.getChildInterventions(childId)
      ]);
      setChild(childRes.data);
      setScreenings(screeningsRes.data);
      setInterventions(interventionsRes.data);
    } catch (error) {
      console.error('Error fetching child data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRiskColor = (risk) => {
    if (risk === 'high') return 'bg-red-100 text-red-800 border-red-300';
    if (risk === 'moderate') return 'bg-yellow-100 text-yellow-800 border-yellow-300';
    return 'bg-green-100 text-green-800 border-green-300';
  };

  const getAgeDisplay = (months) => {
    const years = Math.floor(months / 12);
    const remainingMonths = months % 12;
    if (years > 0) {
      return `${years} year${years > 1 ? 's' : ''} ${remainingMonths} month${remainingMonths !== 1 ? 's' : ''}`;
    }
    return `${months} month${months !== 1 ? 's' : ''}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF9]">
        <Navbar user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center h-96">
          <div className="text-xl font-bold text-[#0F4C81]">{t('common.loading')}</div>
        </div>
      </div>
    );
  }

  if (!child) {
    return (
      <div className="min-h-screen bg-[#FAFAF9]">
        <Navbar user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center h-96">
          <div className="text-xl text-[#EF4444]">Child not found</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-[#FAFAF9]" data-testid="child-profile">
      <Navbar user={user} onLogout={onLogout} />

      <div className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <button
          onClick={() => navigate('/dashboard')}
          data-testid="back-button"
          className="flex items-center space-x-2 text-[#0F4C81] font-semibold mb-6 hover:underline"
        >
          <ArrowLeft size={20} />
          <span>{t('child.backToDashboard')}</span>
        </button>

        {/* Child Info Card */}
        <div className="bg-white rounded-2xl border-2 border-slate-100 p-8 shadow-sm mb-8" data-testid="child-info-card">
          <div className="flex flex-col md:flex-row md:items-start justify-between mb-6">
            <div className="flex items-start space-x-4 mb-4 md:mb-0">
              <div className="w-20 h-20 bg-gradient-to-br from-[#0F4C81] to-[#1e5a9a] rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-3xl">{child.name.charAt(0)}</span>
              </div>
              <div>
                <h1 className="text-4xl font-bold text-[#111827] mb-2 font-['Nunito']" data-testid="child-name">{child.name}</h1>
                <div className="flex flex-wrap gap-3">
                  <span className="flex items-center space-x-1 text-sm text-[#4B5563]">
                    <Calendar size={16} />
                    <span>{getAgeDisplay(child.age_months)}</span>
                  </span>
                  <span className="flex items-center space-x-1 text-sm text-[#4B5563]">
                    <User size={16} />
                    <span>{child.guardian_name}</span>
                  </span>
                  {child.location && (
                    <span className="flex items-center space-x-1 text-sm text-[#4B5563]">
                      <MapPin size={16} />
                      <span>{child.location}</span>
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => navigate(`/screening/${child.id}`)}
                data-testid="new-screening-button"
                className="px-6 py-3 bg-[#3B82F6] text-white rounded-full font-bold hover:bg-[#2563EB] transition-all active:scale-95 shadow-md"
              >
                {t('child.newScreening')}
              </button>
              <button
                onClick={() => navigate(`/speech/${child.id}`)}
                data-testid="speech-analysis-button"
                className="flex items-center space-x-2 px-6 py-3 bg-[#8B5CF6] text-white rounded-full font-bold hover:bg-[#7C3AED] transition-all active:scale-95 shadow-md"
              >
                <Mic size={18} />
                <span>Speech Analysis</span>
              </button>
              <button
                onClick={() => navigate(`/interventions/${child.id}`)}
                data-testid="view-plans-button"
                className="px-6 py-3 bg-[#10B981] text-white rounded-full font-bold hover:bg-[#059669] transition-all active:scale-95 shadow-md"
              >
                {t('child.viewPlans')}
              </button>
              {(user.role === 'frontline_worker' || user.role === 'administrator') && (
                <button
                  onClick={handleSendReminder}
                  disabled={sendingReminder}
                  data-testid="send-reminder-button"
                  className="flex items-center space-x-2 px-6 py-3 bg-[#F59E0B] text-black rounded-full font-bold hover:bg-[#D97706] transition-all active:scale-95 shadow-md disabled:opacity-50"
                >
                  <Bell size={18} />
                  <span>{sendingReminder ? t('common.loading') : t('notifications.sendReminder')}</span>
                </button>
              )}
            </div>
          </div>

          {/* Latest Risk Assessment */}
          {screenings.length > 0 && (
            <div className="pt-6 border-t border-slate-200">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-[#4B5563] uppercase tracking-wider">{t('child.latestRiskAssessment')}</span>
                <span className={`text-sm px-4 py-2 rounded-full font-bold border-2 ${getRiskColor(screenings[0].risk_level)}`}>
                  {screenings[0].risk_level?.toUpperCase() || t('child.notAssessed')}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Screenings & Progress Tabs */}
        <div className="mb-8">
          {/* Tab Navigation */}
          <div className="flex space-x-4 mb-6">
            <button
              onClick={() => setActiveTab('screenings')}
              data-testid="screenings-tab"
              className={`flex items-center space-x-2 px-6 py-3 rounded-xl font-bold transition-all ${
                activeTab === 'screenings'
                  ? 'bg-[#0F4C81] text-white shadow-md'
                  : 'bg-white text-[#4B5563] border-2 border-slate-200 hover:border-[#0F4C81]'
              }`}
            >
              <Activity size={20} />
              <span>{t('child.screeningHistory')}</span>
            </button>
            <button
              onClick={() => setActiveTab('progress')}
              data-testid="progress-tab"
              className={`flex items-center space-x-2 px-6 py-3 rounded-xl font-bold transition-all ${
                activeTab === 'progress'
                  ? 'bg-[#0F4C81] text-white shadow-md'
                  : 'bg-white text-[#4B5563] border-2 border-slate-200 hover:border-[#0F4C81]'
              }`}
            >
              <TrendingUp size={20} />
              <span>{t('progress.viewProgress')}</span>
            </button>
            <button
              onClick={() => setActiveTab('recommendations')}
              data-testid="recommendations-tab"
              className={`flex items-center space-x-2 px-6 py-3 rounded-xl font-bold transition-all ${
                activeTab === 'recommendations'
                  ? 'bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9] text-white shadow-md'
                  : 'bg-white text-[#4B5563] border-2 border-slate-200 hover:border-[#8B5CF6]'
              }`}
            >
              <Sparkles size={20} />
              <span>AI Recommendations</span>
            </button>
          </div>

          {/* Tab Content */}
          {activeTab === 'screenings' ? (
            <>
              {screenings.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-2xl border-2 border-slate-100" data-testid="no-screenings-message">
                  <Activity className="mx-auto text-[#9CA3AF] mb-4" size={64} />
                  <p className="text-lg text-[#4B5563] mb-4">{t('child.noScreenings')}</p>
                  <button
                    onClick={() => navigate(`/screening/${child.id}`)}
                    data-testid="first-screening-button"
                    className="px-6 py-3 bg-[#3B82F6] text-white rounded-full font-bold hover:bg-[#2563EB] transition-all active:scale-95"
                  >
                    {t('child.conductFirstScreening')}
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {screenings.map((screening) => (
                    <div
                      key={screening.id}
                      data-testid={`screening-card-${screening.id}`}
                      className="rounded-2xl border-2 border-slate-100 bg-white p-6 shadow-sm"
                    >
                      <div className="flex flex-col md:flex-row md:items-center justify-between">
                        <div className="flex-1 mb-4 md:mb-0">
                          <div className="flex items-center space-x-3 mb-2">
                            <span className="text-lg font-bold text-[#111827]">{screening.assessment_type}</span>
                            <span className={`text-xs px-3 py-1 rounded-full font-bold border-2 ${getRiskColor(screening.risk_level)}`}>
                              {screening.risk_level?.toUpperCase()}
                            </span>
                            {/* Confidence Score Badge */}
                            {screening.confidence_score && (
                              <span className="text-xs px-3 py-1 rounded-full bg-blue-100 text-blue-800 border-2 border-blue-300 font-semibold" data-testid="confidence-score">
                                {Math.round(screening.confidence_score * 100)}% confidence
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-[#4B5563] mb-2">
                            {t('child.conductedOn')}: {new Date(screening.created_at).toLocaleDateString()}
                          </p>
                          {/* Explainable AI Component */}
                          {screening.ai_analysis && (
                            <div className="mt-4">
                              <ExplainableAI analysis={screening.ai_analysis} />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : activeTab === 'progress' ? (
            <ProgressChart childId={childId} />
          ) : (
            <DynamicRecommendations childId={childId} />
          )}
        </div>

        {/* Intervention Plans */}
        <div>
          <h2 className="text-3xl font-bold text-[#111827] mb-6 font-['Nunito']" data-testid="interventions-title">{t('child.interventionPlans')}</h2>
          {interventions.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-2xl border-2 border-slate-100" data-testid="no-interventions-message">
              <p className="text-lg text-[#4B5563]">{t('child.noInterventions')}</p>
            </div>
          ) : (
            <div className="space-y-4">
              {interventions.map((intervention) => (
                <div
                  key={intervention.id}
                  data-testid={`intervention-card-${intervention.id}`}
                  className="rounded-2xl border-2 border-slate-100 bg-white p-6 shadow-sm"
                >
                  <div className="mb-4">
                    <h3 className="text-xl font-bold text-[#111827] mb-2 font-['Nunito']">{t('intervention.planNumber')}</h3>
                    <p className="text-sm text-[#4B5563]">{t('intervention.created')}: {new Date(intervention.created_at).toLocaleDateString()}</p>
                    <p className="text-sm text-[#4B5563]">{t('intervention.followUp')}: {intervention.follow_up_date}</p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-bold text-[#111827] mb-2">{t('child.riskAreas')}</h4>
                      <ul className="space-y-1">
                        {intervention.risk_areas?.map((area, idx) => (
                          <li key={idx} className="text-sm text-[#4B5563] flex items-center space-x-2">
                            <span className="w-2 h-2 bg-[#EF4444] rounded-full"></span>
                            <span>{area}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-bold text-[#111827] mb-2">{t('child.recommendedActivities')}</h4>
                      <ul className="space-y-2">
                        {intervention.activities?.slice(0, 3).map((activity, idx) => (
                          <li key={idx} className="text-sm p-2 bg-[#F3F4F6] rounded-lg">
                            <p className="font-semibold text-[#111827]">{activity.name}</p>
                            <p className="text-[#4B5563]">{activity.frequency}</p>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      
      {/* Copyright Footer */}
      <footer className="bg-[#F3F4F6] border-t border-gray-200 py-3 text-center">
        <p className="text-sm text-[#6B7280]">© 2025 Hamrith Consulting Services LLP (HCS). All Rights Reserved.</p>
      </footer>
    </div>
  );
};

export default ChildProfile;
